import { TemplateResult, html, LitElement } from 'lit';
import globalStyles from './styles/outline-search.global.css?inline';
import encapsulatedStyles from './styles/outline-search.encapsulated.css?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { customElement, property } from 'lit/decorators.js';
import {
  screenSizeMap,
  ScreenSizeController,
} from '../../controllers/screen-size-controller';
import '../outline-container/outline-container';

export const searchFormVariants = ['default', 'main-header'] as const;
export type SearchFormVariant = (typeof searchFormVariants)[number];


/**
 * The Outline Search component
 * @element outline-search
 *
 */
@customElement('outline-search')
export class OutlineSearch extends LitElement {

  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
    adoptedStyleSheets = new AdoptedStyleSheets(this, {
      globalCSS: globalStyles,
      encapsulatedCSS: encapsulatedStyles,
    });
  
  screens = new ScreenSizeController(this);

  @property({type: String })
  variant: SearchFormVariant;

  @property({ type: Boolean, attribute: 'trap-focus', reflect: true })
  trapFocus = false;

  @property({ type: Boolean })
  isMobile: boolean = window.innerWidth < (screenSizeMap.get('lg') as number);


  render(): TemplateResult {
    return html`
    <div class="outline-search">
      <slot></slot>
    </div>
    `;
  }

  updated() {
    if (this.trapFocus) {
      return this.applyHandlers();
    } else {
      return this.removeHandlers();
    }
  }

  applyHandlers() {
    const elementRef = this.getElementRefs();
    elementRef.openRef?.addEventListener('keydown', this.openFocusHandler);
    elementRef.submitRef?.addEventListener('keydown', this.submitFocusHandler);
    elementRef.inputRef?.addEventListener('keydown', this.inputFocusHandler);
  }
  removeHandlers() {
    const elementRef = this.getElementRefs();
    elementRef.openRef?.removeEventListener('keydown', this.openFocusHandler);
    elementRef.submitRef?.removeEventListener('keydown', this.submitFocusHandler);
    elementRef.inputRef?.removeEventListener('keydown', this.inputFocusHandler);
  }

  getElementRefs() {
    const refsObject: Record<string, HTMLElement> = {};  
    const header = document.querySelector('outline-main-header')?.shadowRoot?.querySelector('outline-container') as HTMLElement;
    const openCloseButton = header?.querySelector(
      '.main-header--search-button'
    ) as HTMLButtonElement;
    const submitButton = this.querySelector('[aria-label="Perform search"]') as HTMLElement;
    const inputForm = this.querySelector('#header-search-query') as HTMLElement;
      if (openCloseButton) {
        refsObject['openRef'] = openCloseButton;
      }
      if (submitButton) {
        refsObject['submitRef'] = submitButton;
      }
      if (inputForm) {
        refsObject['inputRef'] = inputForm;
      }

      return refsObject;
  }

  openFocusHandler(e:KeyboardEvent) {
    const self = document.querySelector('outline-search[trap-focus]') as OutlineSearch;
    
    if (e.key === `Tab`) {
      e.preventDefault();      
      self.getElementRefs().inputRef.focus();
    } 
    if (e.shiftKey && e.key === 'Tab') {
      e.preventDefault();
      self.getElementRefs().submitRef.focus();
    }
  }
  submitFocusHandler(e:KeyboardEvent) {
    const self = document.querySelector('outline-search[trap-focus]') as OutlineSearch;
    
    if (e.key === `Tab`) {
      e.preventDefault();      
      self.getElementRefs().openRef.focus();
    } 
    if (e.shiftKey && e.key === 'Tab') {
      e.preventDefault();
      self.getElementRefs().inputRef.focus();
    }
  }
  inputFocusHandler(e:KeyboardEvent) {
    const self = document.querySelector('outline-search[trap-focus]') as OutlineSearch;
    
    if (e.key === `Tab`) {
      e.preventDefault();      
      self.getElementRefs().submitRef.focus();
    } 
    if (e.shiftKey && e.key === 'Tab') {
      e.preventDefault();
      self.getElementRefs().openRef.focus();
    }
  }
} 
